// The local outbox: every answer lands on the device first, in one
// IndexedDB transaction, and waits there until the syncer has it confirmed.
// The rules (which answers a change erases, what counts as done) come from
// poweryourflow-lib; this file only stores their result.

import { applyScreen, isComplete, progress } from "poweryourflow-lib";
import type { FieldValues } from "poweryourflow-lib";
import { LIMITS } from "../schema/types";
import { META, RUNS, deviceId, getAccountRuns, getRun, inTransaction } from "./db";
import type { LocalRun, PendingOp } from "./db";
import { RUN_OUTBOX_MAX } from "./policy";
import type { AnswerValue, AssessmentId, AssessmentRun, QuestionnaireLookup } from "./types";

export interface OutboxDeps {
  db: () => Promise<IDBDatabase>;
  questionnaireFor: QuestionnaireLookup;
  now: () => number;
}

function questionnaire(deps: OutboxDeps, assessmentId: AssessmentId) {
  const found = deps.questionnaireFor(assessmentId);
  if (!found) throw new Error(`no questionnaire for ${assessmentId}`);
  return found;
}

/** A run that does not exist yet: version 1, nothing answered. */
function freshRun(accountId: string, assessmentId: AssessmentId, device: string, now: number): AssessmentRun {
  return {
    accountId,
    assessmentId,
    version: 1,
    state: "todo",
    answers: {},
    answeredCount: 0,
    visibleTotal: 0,
    startedAt: now,
    deviceId: device,
    answeredAt: now,
    // Server clock at the last write; the server has not written it yet.
    updatedAt: 0,
  };
}

/**
 * Queues an operation on a run. A save or finish of the version already
 * last in the queue replaces it: the server takes the whole answer set,
 * so only the latest one needs to travel.
 */
function enqueue(record: LocalRun, op: PendingOp): void {
  const last = record.outbox.at(-1);
  if (last && last.kind !== "redo" && op.kind !== "redo" && last.request.version === op.request.version) {
    record.outbox[record.outbox.length - 1] = op;
    return;
  }
  if (record.outbox.length >= RUN_OUTBOX_MAX) {
    throw new Error(`${record.assessmentId}: ${RUN_OUTBOX_MAX} operations already wait for the network`);
  }
  record.outbox.push(op);
}

/**
 * Records one screen (its answer and its fields) and returns the run as it now stands on the device.
 * The read, the lib's rules and the write share one transaction, so two
 * quick taps cannot interleave and lose an answer.
 */
export async function recordAnswer(
  deps: OutboxDeps,
  accountId: string,
  assessmentId: AssessmentId,
  questionId: string,
  value: AnswerValue,
  fields: FieldValues = {},
): Promise<AssessmentRun> {
  const items = questionnaire(deps, assessmentId);
  const db = await deps.db();
  return inTransaction(db, [RUNS, META], "readwrite", async (tx) => {
    const device = await deviceId(tx);
    const now = deps.now();
    const record: LocalRun = (await getRun(tx, accountId, assessmentId)) ?? {
      accountId,
      assessmentId,
      run: freshRun(accountId, assessmentId, device, now),
      revision: 0,
      outbox: [],
    };
    if (record.run.state === "done") throw new Error(`${assessmentId} v${record.run.version} is finished`);

    const answers = applyScreen(items, record.run.answers, questionId, value, fields);
    const { answered, total } = progress(items, answers);
    record.revision += 1;
    record.run = {
      ...record.run,
      state: "progress",
      answers,
      answeredCount: answered,
      visibleTotal: total,
      deviceId: device,
      answeredAt: now,
    };
    const { version } = record.run;
    enqueue(record, {
      kind: "save",
      rev: record.revision,
      request: { assessmentId, version, answers, deviceId: device, answeredAt: now },
    });
    tx.objectStore(RUNS).put(record);
    return record.run;
  });
}

/** Marks the run finished on the device and queues the finish. Throws when the lib says it is not complete. */
export async function recordFinish(deps: OutboxDeps, accountId: string, assessmentId: AssessmentId): Promise<AssessmentRun> {
  const items = questionnaire(deps, assessmentId);
  const db = await deps.db();
  return inTransaction(db, [RUNS], "readwrite", async (tx) => {
    const record = await getRun(tx, accountId, assessmentId);
    if (!record || record.run.state !== "progress") throw new Error(`${assessmentId} has no run in progress`);
    if (!isComplete(items, record.run.answers)) throw new Error(`${assessmentId} v${record.run.version} is not complete`);

    const finishedAt = deps.now();
    const { version, answers, deviceId: device, answeredAt } = record.run;
    record.revision += 1;
    record.run = { ...record.run, state: "done", finishedAt };
    enqueue(record, {
      kind: "finish",
      rev: record.revision,
      request: { assessmentId, version, answers, deviceId: device, answeredAt, finishedAt },
    });
    tx.objectStore(RUNS).put(record);
    return record.run;
  });
}

/**
 * Opens the next version of a finished assessment on the device. The redo
 * is queued before any answer of the new version, so the server opens the
 * version before it receives answers for it.
 */
export async function requestRedo(deps: OutboxDeps, accountId: string, assessmentId: AssessmentId): Promise<AssessmentRun> {
  const db = await deps.db();
  return inTransaction(db, [RUNS], "readwrite", async (tx) => {
    const record = await getRun(tx, accountId, assessmentId);
    if (!record || record.run.state !== "done") throw new Error(`${assessmentId} has no finished run to redo`);
    const old = record.run;
    if (old.version >= LIMITS.MAX_RUN_VERSION) throw new Error(`${assessmentId} reached LIMITS.MAX_RUN_VERSION`);

    const items = deps.questionnaireFor(assessmentId);
    const now = deps.now();
    const { finishedAt, score: _score, previousFinishedAt: _previous, ...kept } = old;
    record.revision += 1;
    record.run = {
      ...kept,
      version: old.version + 1,
      state: "todo",
      answers: {},
      answeredCount: 0,
      visibleTotal: items ? progress(items, {}).total : 0,
      startedAt: now,
      ...(finishedAt !== undefined ? { previousFinishedAt: finishedAt } : {}),
    };
    enqueue(record, { kind: "redo", rev: record.revision, request: { assessmentId, fromVersion: old.version } });
    tx.objectStore(RUNS).put(record);
    return record.run;
  });
}

/** How many of the account's runs hold changes the server has not confirmed. */
export async function pendingAnswers(deps: Pick<OutboxDeps, "db">, accountId: string): Promise<number> {
  const db = await deps.db();
  const runs = await inTransaction(db, [RUNS], "readonly", (tx) => getAccountRuns(tx, accountId));
  return runs.filter((record) => record.outbox.length > 0).length;
}
